import styled from "styled-components";
import { usePage, router } from "@inertiajs/react";
import { useLandingLanguage } from "../../../contexts/landing-language";
import { DynamicIcon } from "../../../components/icons";
import { unmodule } from "../../../util";

const FooterSection = styled.footer`
  background: #1a1a1a;
  color: #faf8f5;
  padding: 100px 24px 0;
`;

const Container = styled.div`
  max-width: 1300px;
  margin: 0 auto;
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 1.2fr;
  gap: 60px;
  margin-bottom: 80px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr 1fr;
    gap: 48px;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FooterColumn = styled.div`
  h3 {
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 28px;
    color: #c9a86c;
    letter-spacing: 2px;
    text-transform: uppercase;
  }

  p {
    font-size: 14px;
    line-height: 1.9;
    color: #999;
    margin-bottom: 24px;
    font-weight: 300;
  }
`;

const Logo = styled.div`
  margin-bottom: 24px;

  img {
    height: 60px;
    width: auto;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const ContactItem = styled.a`
  display: flex;
  align-items: center;
  gap: 14px;
  color: #999;
  font-size: 14px;
  transition: all 0.3s;
  font-weight: 300;

  &:hover {
    color: #c9a86c;
  }

  svg {
    color: #c9a86c;
    min-width: 18px;
    width: 18px;
    height: 18px;
  }
`;

const FooterLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FooterLink = styled.a`
  color: #999;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 300;

  &:hover {
    color: #c9a86c;
    padding-left: 5px;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const SocialIcon = styled.a`
  width: 44px;
  height: 44px;
  border: 1px solid #333;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: #c9a86c;
    border-color: #c9a86c;
    color: #1a1a1a;
    transform: translateY(-4px);
  }
`;

const Newsletter = styled.div`
  margin-top: 28px;
`;

const NewsletterForm = styled.form`
  display: flex;
  gap: 0;
  margin-top: 16px;
`;

const NewsletterInput = styled.input`
  flex: 1;
  padding: 14px 18px;
  border: 1px solid #333;
  border-right: none;
  background: #2a2a2a;
  color: #faf8f5;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #c9a86c;
  }

  &::placeholder {
    color: #666;
  }
`;

const SubscribeButton = styled.button`
  background: #c9a86c;
  color: #1a1a1a;
  border: 1px solid #c9a86c;
  padding: 14px 24px;
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s;
  white-space: nowrap;
  letter-spacing: 1px;
  text-transform: uppercase;

  &:hover {
    background: transparent;
    color: #c9a86c;
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid #2a2a2a;
  padding: 36px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 640px) {
    flex-direction: column;
    text-align: center;
  }
`;

const Copyright = styled.div`
  font-size: 13px;
  color: #666;
  font-weight: 300;
`;

const FooterBottomLinks = styled.div`
  display: flex;
  gap: 32px;

  a {
    font-size: 13px;
    color: #666;
    transition: color 0.3s;
    font-weight: 300;

    &:hover {
      color: #c9a86c;
    }
  }
`;

const SocialIcons = ({ socials }) => {
  if (!socials || socials.length === 0) return null;

  return (
    <SocialLinks>
      {socials.map((social) => (
        <SocialIcon
          key={social.icon_map}
          href={social.link}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={social.alt}
        >
          <DynamicIcon name={social.icon_map} size={20} />
        </SocialIcon>
      ))}
    </SocialLinks>
  );
};

const Footer = () => {
  const { landing_content } = usePage().props;
  const { t, language } = useLandingLanguage();
  const f = t.footer;
  const socials = landing_content?.socials || [];
  const contactUs = landing_content?.contact_us || [];

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    const email = e.target.email.value;
    console.log("[v0] Newsletter subscription:", email);
    alert(`Thank you for subscribing with ${email}!`);
    e.target.reset();
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    } else {
      const homeUrl = language === "id" ? `/id/hebe#${id}` : `/hebe#${id}`;
      router.visit(homeUrl);
    }
  };

  return (
    <FooterSection id="contact">
      <Container>
        <FooterGrid>
          <FooterColumn>
            <Logo>
              <img
                src={unmodule(
                  require("../../../assets/logo-font-horizontal-white.png"),
                )}
                alt="HEBE logo horizontal"
              />
            </Logo>
            <p>{f.description}</p>
            <SocialIcons socials={socials} />
          </FooterColumn>

          <FooterColumn>
            <h3>{f.quickLinks}</h3>
            <FooterLinks>
              <FooterLink onClick={() => scrollToSection("about")}>
                {f.links.about}
              </FooterLink>
              <FooterLink onClick={() => scrollToSection("rooms")}>
                {f.links.rooms}
              </FooterLink>
              <FooterLink onClick={() => scrollToSection("facilities")}>
                {f.links.facilities}
              </FooterLink>
              <FooterLink onClick={() => scrollToSection("gallery")}>
                {f.links.gallery}
              </FooterLink>
              <FooterLink
                onClick={() =>
                  router.visit(language === "id" ? "/id/insights" : "/insights")
                }
              >
                {t.nav.insights}
              </FooterLink>
              <FooterLink onClick={() => scrollToSection("booking")}>
                {t.nav.bookNow}
              </FooterLink>
            </FooterLinks>
          </FooterColumn>

          <FooterColumn>
            <h3>{f.services}</h3>
            <FooterLinks>
              <FooterLink href="#">{f.serviceLinks.fnb}</FooterLink>
              <FooterLink href="#">{f.serviceLinks.roomService}</FooterLink>
              <FooterLink
                onClick={() =>
                  router.visit(
                    language === "id"
                      ? "/id/insights/c/activities"
                      : "/insights/c/activities",
                  )
                }
              >
                {f.serviceLinks.activities}
              </FooterLink>
              <FooterLink
                onClick={() =>
                  router.visit(
                    language === "id"
                      ? "/id/insights/c/eco-tours"
                      : "/insights/c/eco-tours",
                  )
                }
              >
                {f.serviceLinks.ecoTours}
              </FooterLink>
            </FooterLinks>
          </FooterColumn>

          <FooterColumn>
            <h3>{f.contactUs}</h3>
            <ContactInfo>
              {contactUs.map((item) => (
                <ContactItem
                  key={item.icon_map}
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <DynamicIcon name={item.icon_map} size={20} />
                  <span>{item.text}</span>
                </ContactItem>
              ))}
            </ContactInfo>
            <Newsletter>
              <p style={{ fontSize: "14px", marginBottom: "12px" }}>
                {f.newsletterDesc}
              </p>
              <NewsletterForm onSubmit={handleNewsletterSubmit}>
                <NewsletterInput
                  type="email"
                  name="email"
                  placeholder={f.emailPlaceholder}
                  required
                />
                {/*
                  <SubscribeButton type="submit">{f.subscribe}</SubscribeButton>
                */}
              </NewsletterForm>
            </Newsletter>
          </FooterColumn>
        </FooterGrid>

        <FooterBottom>
          <Copyright>{f.copyright}</Copyright>
          <FooterBottomLinks>
            <a
              href={
                language === "id"
                  ? "/id/hebe/privacy-policy"
                  : "/hebe/privacy-policy"
              }
            >
              {f.privacyPolicy}
            </a>
            <a
              href={
                language === "id"
                  ? "/id/hebe/terms-of-service"
                  : "/hebe/terms-of-service"
              }
            >
              {f.termsOfService}
            </a>
          </FooterBottomLinks>
        </FooterBottom>
      </Container>
    </FooterSection>
  );
};

export default Footer;
